import { useState } from "react";
import { useList, useCreate, useUpdate, useRemove } from "@/hooks/crud";
import { Modal } from "@/components/Modal";
import { TextField, TextAreaField, SelectField } from "@/components/Fields";
import { StatusBadge } from "@/components/Badges";
import { ErrorBanner } from "@/components/Feedback";
import { PROMPT_STATUSES, GENERATION_STATUSES } from "@/lib/enums";
import { msToClock } from "@/lib/format";
import type { TrackPrompt, TrackGeneration } from "@/types/database";

/* ── Prompt form ──────────────────────────────────────────────────────── */
function PromptForm({
  trackId,
  prompt,
  nextVersion,
  onClose,
}: {
  trackId: number;
  prompt: TrackPrompt | null;
  nextVersion: number;
  onClose: () => void;
}) {
  const create = useCreate("track_prompts");
  const update = useUpdate("track_prompts");
  const [versionNo, setVersionNo] = useState(
    prompt?.version_no != null ? String(prompt.version_no) : String(nextVersion),
  );
  const [style, setStyle] = useState(prompt?.style_field ?? "");
  const [lyrics, setLyrics] = useState(prompt?.lyrics_field ?? "");
  const [audioUrl, setAudioUrl] = useState(prompt?.audio_influence_url ?? "");
  const [sunoModel, setSunoModel] = useState(prompt?.suno_model ?? "");
  const [status, setStatus] = useState(prompt?.status ?? "DRAFT");
  const [isSelected, setIsSelected] = useState(prompt?.is_selected ?? false);
  const [notes, setNotes] = useState(prompt?.notes ?? "");
  const mutation = prompt ? update : create;

  async function save() {
    const body = {
      track_id: trackId,
      version_no: Number(versionNo) || nextVersion,
      style_field: style.trim() || null,
      lyrics_field: lyrics.trim() || null,
      audio_influence_url: audioUrl.trim() || null,
      suno_model: sunoModel.trim() || null,
      status,
      is_selected: isSelected,
      notes: notes.trim() || null,
    };
    if (prompt) await update.mutateAsync({ id: prompt.id, body });
    else await create.mutateAsync(body);
    onClose();
  }

  return (
    <Modal
      title={prompt ? `Edit Prompt v${prompt.version_no}` : "New Prompt Version"}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      {mutation.error && <ErrorBanner error={mutation.error} />}
      <div className="field-row">
        <TextField label="Version #" value={versionNo} onChange={setVersionNo} type="number" />
        <SelectField label="Status" value={status} onChange={setStatus} options={PROMPT_STATUSES} />
      </div>
      <TextAreaField
        label="Style field (Suno @ 100%)"
        value={style}
        onChange={setStyle}
        rows={4}
      />
      <TextAreaField label="Lyrics field" value={lyrics} onChange={setLyrics} rows={4} />
      <div className="field-row">
        <TextField label="Audio influence URL" value={audioUrl} onChange={setAudioUrl} />
        <TextField label="Suno model" value={sunoModel} onChange={setSunoModel} />
      </div>
      <div className="field">
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => setIsSelected(e.target.checked)}
            style={{ width: "auto" }}
          />
          Selected version
        </label>
      </div>
      <TextAreaField label="Notes" value={notes} onChange={setNotes} rows={2} />
    </Modal>
  );
}

/* ── Generation form ──────────────────────────────────────────────────── */
function GenerationForm({
  trackId,
  promptId,
  generation,
  onClose,
}: {
  trackId: number;
  promptId: number;
  generation: TrackGeneration | null;
  onClose: () => void;
}) {
  const create = useCreate("track_generations");
  const update = useUpdate("track_generations");
  const [label, setLabel] = useState(generation?.label ?? "");
  const [clipUrl, setClipUrl] = useState(generation?.suno_clip_url ?? "");
  const [audioUrl, setAudioUrl] = useState(generation?.audio_url ?? "");
  const [status, setStatus] = useState(generation?.status ?? "GENERATED");
  const [isSelected, setIsSelected] = useState(generation?.is_selected ?? false);
  const [notes, setNotes] = useState(generation?.notes ?? "");
  const mutation = generation ? update : create;

  async function save() {
    const body = {
      track_id: trackId,
      prompt_id: promptId,
      label: label.trim() || null,
      suno_clip_url: clipUrl.trim() || null,
      audio_url: audioUrl.trim() || null,
      status,
      is_selected: isSelected,
      notes: notes.trim() || null,
    };
    if (generation) await update.mutateAsync({ id: generation.id, body });
    else await create.mutateAsync(body);
    onClose();
  }

  return (
    <Modal
      title={generation ? "Edit Generation" : "New Generation"}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      {mutation.error && <ErrorBanner error={mutation.error} />}
      <div className="field-row">
        <TextField label="Label" value={label} onChange={setLabel} />
        <SelectField label="Status" value={status} onChange={setStatus} options={GENERATION_STATUSES} />
      </div>
      <TextField label="Suno clip URL" value={clipUrl} onChange={setClipUrl} />
      <TextField label="Audio URL" value={audioUrl} onChange={setAudioUrl} />
      <div className="field">
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => setIsSelected(e.target.checked)}
            style={{ width: "auto" }}
          />
          Selected take
        </label>
      </div>
      <TextAreaField label="Notes" value={notes} onChange={setNotes} rows={2} />
    </Modal>
  );
}

/* ── Generations under a prompt ───────────────────────────────────────── */
function Generations({ trackId, promptId }: { trackId: number; promptId: number }) {
  const { data } = useList("track_generations", `prompt_id=eq.${promptId}&order=created_at.desc`);
  const gens = (data?.rows ?? []) as TrackGeneration[];
  const remove = useRemove("track_generations");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TrackGeneration | null>(null);

  return (
    <div style={{ marginTop: 10, borderTop: "1px solid var(--border)", paddingTop: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <span className="muted mono" style={{ fontSize: 11 }}>
          GENERATIONS ({gens.length})
        </span>
        <button className="btn btn-sm btn-ghost" onClick={() => setCreating(true)}>
          + Generation
        </button>
      </div>
      {gens.map((g) => (
        <div
          key={g.id}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
            padding: "6px 8px",
            background: "var(--void)",
            borderRadius: 6,
            marginBottom: 6,
          }}
        >
          <div style={{ fontSize: 12 }}>
            <StatusBadge status={g.status} /> {g.label ?? "(unlabeled)"}
            {g.is_selected && <span className="badge badge-remix" style={{ marginLeft: 6 }}>★</span>}
            {g.duration_ms ? <span className="muted"> · {msToClock(g.duration_ms)}</span> : null}
            {g.suno_clip_url && (
              <>
                {" · "}
                <a href={g.suno_clip_url} target="_blank" rel="noreferrer">
                  clip
                </a>
              </>
            )}
          </div>
          <div className="row-actions">
            <button className="btn btn-sm btn-ghost" onClick={() => setEditing(g)}>
              Edit
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={() => {
                if (confirm("Delete this generation?")) remove.mutate(g.id);
              }}
            >
              ✕
            </button>
          </div>
        </div>
      ))}
      {creating && (
        <GenerationForm
          trackId={trackId}
          promptId={promptId}
          generation={null}
          onClose={() => setCreating(false)}
        />
      )}
      {editing && (
        <GenerationForm
          trackId={trackId}
          promptId={promptId}
          generation={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/* ── Panel ────────────────────────────────────────────────────────────── */
export function PromptsPanel({ trackId }: { trackId: number }) {
  const { data } = useList("track_prompts", `track_id=eq.${trackId}&order=version_no.asc`);
  const prompts = (data?.rows ?? []) as TrackPrompt[];
  const remove = useRemove("track_prompts");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TrackPrompt | null>(null);
  const nextVersion = prompts.reduce((m, p) => Math.max(m, p.version_no), 0) + 1;

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>
          Prompts <span className="muted mono">{prompts.length}</span>
        </h3>
        <button className="btn btn-sm" onClick={() => setCreating(true)}>
          + New Version
        </button>
      </div>
      <div className="panel-body">
        {prompts.length === 0 && <span className="muted">No prompt versions yet.</span>}
        {prompts.map((p) => (
          <div key={p.id} className="card" style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <div style={{ fontWeight: 600 }}>
                v{p.version_no} <StatusBadge status={p.status} />
                {p.is_selected && (
                  <span className="badge badge-remix" style={{ marginLeft: 6 }}>
                    SELECTED
                  </span>
                )}
                {p.suno_model && <span className="muted"> · {p.suno_model}</span>}
              </div>
              <div className="row-actions">
                <button className="btn btn-sm btn-ghost" onClick={() => setEditing(p)}>
                  Edit
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => {
                    if (confirm(`Delete prompt v${p.version_no} and its generations?`))
                      remove.mutate(p.id);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
            {p.style_field && (
              <div style={{ marginTop: 8 }}>
                <div className="muted mono" style={{ fontSize: 10 }}>
                  STYLE
                </div>
                <div style={{ fontSize: 13 }}>{p.style_field}</div>
              </div>
            )}
            {p.lyrics_field && (
              <div style={{ marginTop: 8 }}>
                <div className="muted mono" style={{ fontSize: 10 }}>
                  LYRICS
                </div>
                <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{p.lyrics_field}</div>
              </div>
            )}
            <Generations trackId={trackId} promptId={p.id} />
          </div>
        ))}
      </div>
      {creating && (
        <PromptForm
          trackId={trackId}
          prompt={null}
          nextVersion={nextVersion}
          onClose={() => setCreating(false)}
        />
      )}
      {editing && (
        <PromptForm
          trackId={trackId}
          prompt={editing}
          nextVersion={nextVersion}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
